'use client';

import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import React, { useEffect } from 'react';
import useCookieContext from '@/components/providers/context/cookie-context';
import CloseMenuIconButton from '@/components/ui/common/close-button';
import CustomLink from '@/components/ui/common/custom-link';
import Switch from './switch-button';
import { FooterButton } from '@/components/ui/common/buttons';
import { useLocale } from '@/components/providers/context/locale-context';

export default function CookieSetting() {
    const { t } = useTranslation('cookie');
    const locale = useLocale();
    const {
        getCookieSettingsStatus,
        hideCookieSettings,
        createUserConsent,
        updateCookiesRange,
        getCookieRange,
        hideAccept,
    } = useCookieContext();

    const [cookiePerformance, setCookiePerformance] = React.useState(false);
    const [cookiePreference, setCookiePreference] = React.useState(false);

    useEffect(() => {
        const range = getCookieRange();
        if (range === '') {
            return;
        }

        if (range === 'base') {
            setCookiePerformance(false);
            setCookiePreference(false);
        } else if (range === 'all') {
            setCookiePerformance(true);
            setCookiePreference(true);
        } else {
            const ranges = range.split(':');
            setCookiePerformance(ranges.includes('porformance'));
            setCookiePreference(ranges.includes('preference'));
        }
    }, []);

    const handleAcceptAll = () => {
        setCookiePerformance(true);
        setCookiePreference(true);
        createUserConsent('agree');
        updateCookiesRange('all');
        setTimeout(() => {
            hideCookieSettings();
            hideAccept();
        }, 500);
    };

    const handleRejectAll = () => {
        setCookiePerformance(false);
        setCookiePreference(false);
        createUserConsent('reject');
        updateCookiesRange('none');
        setTimeout(() => {
            hideCookieSettings();
            hideAccept();
        }, 500);
    };

    const handleAcceptCurrent = () => {
        createUserConsent('agree');
        updateCookiesRange(
            [
                'base',
                cookiePerformance ? 'porformance' : 'no-porformance',
                cookiePreference ? 'preference' : 'no-preference',
            ].join(':'),
        );
        setTimeout(() => {
            hideCookieSettings();
            hideAccept();
        }, 500);
    };

    const handlePerformance = (checked: boolean) => {
        setCookiePerformance(checked);
    };

    const handlePreference = (checked: boolean) => {
        setCookiePreference(checked);
    };

    return (
        <div
            className={clsx(
                'screen-safe fixed left-[50%] top-[50%] z-[9999] flex -translate-x-[50%] -translate-y-[50%] items-center justify-center bg-[#00000020] backdrop-blur-md transition-all',
                {
                    'invisible delay-100': !getCookieSettingsStatus(),
                    visible: getCookieSettingsStatus(),
                },
            )}
        >
            {/* web端 */}
            <div
                className={clsx(
                    'relative hidden h-[690px] w-[650px] flex-row overflow-hidden rounded-[24px] bg-white px-[40px] pb-[24px] pt-[40px] transition-all duration-300 ease-in-out sm:flex',
                    {
                        'invisible scale-90 opacity-0':
                            !getCookieSettingsStatus(),
                        'visible scale-100 opacity-100':
                            getCookieSettingsStatus(),
                    },
                )}
            >
                <div className="flex h-full w-full flex-col items-center justify-between">
                    <div className="flex w-full flex-col">
                        <div className={clsx('j-h5 text-black')}>
                            {t('setting.title')}
                        </div>
                        <div className={clsx('j-t2 mt-[8px] text-[#515151]')}>
                            {t('setting.context.text')}
                            <CustomLink href="/cookie" fill="#515151">
                                {t('setting.context.link')}
                            </CustomLink>
                        </div>

                        <div className="mt-[40px] flex w-full flex-col divide-y-[1px]">
                            <div className="flex flex-row justify-between gap-[32px] py-[16px]">
                                <div className="flex flex-col gap-[8px]">
                                    <div className={clsx('j-h7 text-black')}>
                                        {t('setting.necessary.title')}
                                    </div>
                                    <div
                                        className={clsx('j-t3 text-[#515151]')}
                                    >
                                        {t('setting.necessary.text')}
                                    </div>
                                </div>

                                <Switch disabled checked />
                            </div>
                            <div className="flex flex-row justify-between gap-[32px] py-[16px]">
                                <div className="flex flex-col gap-[8px]">
                                    <div className={clsx('j-h7 text-black')}>
                                        {t('setting.performance.title')}
                                    </div>
                                    <div
                                        className={clsx('j-t3 text-[#515151]')}
                                    >
                                        {t('setting.performance.text')}
                                    </div>
                                </div>

                                <Switch
                                    checked={cookiePerformance}
                                    onChanged={handlePerformance}
                                />
                            </div>
                            <div className="flex flex-row justify-between gap-[32px] py-[16px]">
                                <div className="flex flex-col gap-[8px]">
                                    <div className={clsx('j-h7 text-black')}>
                                        {t('setting.preference.title')}
                                    </div>
                                    <div
                                        className={clsx('j-t3 text-[#515151]')}
                                    >
                                        {t('setting.preference.text')}
                                    </div>
                                </div>

                                <Switch
                                    checked={cookiePreference}
                                    onChanged={handlePreference}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="mt-[40px] flex w-full flex-row items-center justify-between">
                        <div className="flex flex-row gap-[16px]">
                            <FooterButton
                                className={clsx(
                                    'j-t2 h-[36px] w-[136px] sm:h-[40px]',
                                )}
                                iconSize={[15, 15]}
                                iconSize4Mobile={[15, 15]}
                                textColor="#FFFFFF"
                                textHoverColor="#FFFFFF"
                                borderColor="#6038FF"
                                boderHoverColor="#7B5BFF"
                                bgHoverColor="#7B5BFF"
                                bgColor="#6038FF"
                                onClick={handleAcceptAll}
                                text={t('button.accept')}
                                iconColor="#FFFFFF"
                                iconHoverColor="#FFFFFF"
                                withIcon={true}
                                keepCenter={true}
                            />
                            <FooterButton
                                icon={<></>}
                                iconSize={[0, 0]}
                                iconSize4Mobile={[0, 0]}
                                className={clsx(
                                    'j-t2 h-[36px] w-full sm:h-[40px] sm:w-[113px]',
                                )}
                                textColor="#000000"
                                textHoverColor="#FFFFFF"
                                borderColor="#000000"
                                boderHoverColor="#000000"
                                bgHoverColor="#000000"
                                onClick={handleRejectAll}
                                text={t('button.reject')}
                                withIcon={false}
                                keepCenter={true}
                            />
                        </div>
                        <FooterButton
                            icon={<></>}
                            iconSize={[0, 0]}
                            iconSize4Mobile={[0, 0]}
                            className={clsx(
                                'j-t2 h-[36px] w-full sm:h-[40px] sm:w-auto',
                            )}
                            textColor="#000000"
                            textHoverColor="#FFFFFF"
                            borderColor="#000000"
                            boderHoverColor="#000000"
                            bgHoverColor="#000000"
                            onClick={handleAcceptCurrent}
                            text={t('button.acceptCurrent')}
                            withIcon={false}
                            keepCenter={true}
                        />
                    </div>
                </div>
                <div className="absolute right-[28px] top-[28px]">
                    <CloseMenuIconButton
                        fill="#000000"
                        onClose={hideCookieSettings}
                        width={24}
                        height={24}
                    />
                </div>
            </div>

            {/* 移动端 */}
            <div className="tablet:hidden relative flex h-full w-full flex-col overflow-auto bg-white">
                <div className="flex h-full w-full flex-col px-[28px] pt-[72px]">
                    <div className="flex w-full flex-col">
                        <div className={clsx('j-h5 text-black')}>
                            {t('setting.title')}
                        </div>
                        <div className={clsx('j-t2 mt-[8px] text-[#515151]')}>
                            {t('setting.context.text')}
                            <CustomLink
                                href="/cookie"
                                fill="#000000"
                                iconSize={13}
                            >
                                {t('setting.context.link')}
                            </CustomLink>
                        </div>

                        <FooterButton
                            className={clsx(
                                'j-t2 mt-[24px] h-[40px] w-[148px]',
                            )}
                            iconSize={[15, 15]}
                            iconSize4Mobile={[15, 15]}
                            textColor="#FFFFFF"
                            textHoverColor="#FFFFFF"
                            borderColor="#6038FF"
                            boderHoverColor="#7B5BFF"
                            bgHoverColor="#7B5BFF"
                            bgColor="#6038FF"
                            onClick={handleAcceptAll}
                            text={t('button.accept')}
                            iconColor="#FFFFFF"
                            iconHoverColor="#FFFFFF"
                            withIcon={true}
                            keepCenter={true}
                        />
                    </div>

                    <div className="mt-[32px] flex w-full flex-col divide-y-[1px]">
                        <div className="flex flex-row justify-between gap-[32px] py-[16px]">
                            <div className="flex flex-col gap-[8px]">
                                <div className={clsx('j-h7 text-black')}>
                                    {t('setting.necessary.title')}
                                </div>
                                <div className={clsx('j-t3 text-[#515151]')}>
                                    {t('setting.necessary.text')}
                                </div>
                            </div>

                            <Switch disabled checked />
                        </div>
                        <div className="flex flex-row justify-between gap-[32px] py-[16px]">
                            <div className="flex flex-col gap-[8px]">
                                <div className={clsx('j-h7 text-black')}>
                                    {t('setting.performance.title')}
                                </div>
                                <div className={clsx('j-t3 text-[#515151]')}>
                                    {t('setting.performance.text')}
                                </div>
                            </div>

                            <Switch
                                checked={cookiePerformance}
                                onChanged={handlePerformance}
                            />
                        </div>
                        <div className="flex flex-row justify-between gap-[32px] py-[16px]">
                            <div className="flex flex-col gap-[8px]">
                                <div className={clsx('j-h7 text-black')}>
                                    {t('setting.preference.title')}
                                </div>
                                <div className={clsx('j-t3 text-[#515151]')}>
                                    {t('setting.preference.text')}
                                </div>
                            </div>

                            <Switch
                                checked={cookiePreference}
                                onChanged={handlePreference}
                            />
                        </div>
                    </div>

                    <div className="w-full pb-[80px]"></div>
                </div>

                <div className="fixed bottom-0 left-0 right-0 h-[80px] bg-white [border-top:0.5px_solid_#dfdfdf]">
                    <div
                        className={clsx(
                            'j-t1 flex w-full flex-row justify-between gap-[16px] px-[28px] pt-[16px]',
                        )}
                    >
                        <FooterButton
                            icon={<></>}
                            iconSize={[0, 0]}
                            iconSize4Mobile={[0, 0]}
                            className={clsx('j-t2 h-[40px] w-full sm:w-auto')}
                            textColor="#000000"
                            textHoverColor="#FFFFFF"
                            borderColor="#000000"
                            boderHoverColor="#000000"
                            bgHoverColor="#000000"
                            onClick={handleRejectAll}
                            text={t('button.reject')}
                            withIcon={false}
                            keepCenter={true}
                        />
                        <FooterButton
                            icon={<></>}
                            iconSize={[0, 0]}
                            iconSize4Mobile={[0, 0]}
                            className={clsx('j-t2 h-[40px] w-full sm:w-auto')}
                            textColor="#000000"
                            textHoverColor="#FFFFFF"
                            borderColor="#000000"
                            boderHoverColor="#000000"
                            bgHoverColor="#000000"
                            onClick={handleAcceptCurrent}
                            text={t('button.acceptCurrent')}
                            withIcon={false}
                            keepCenter={true}
                        />
                    </div>
                </div>

                <div className="fixed left-0 right-0 top-0 flex h-[56px] items-center justify-end bg-[#ffffffcc] pr-[24px] backdrop-blur-[32px] backdrop-filter [border-bottom:0.5px_solid_#dfdfdf] ">
                    <CloseMenuIconButton
                        fill="#000000"
                        onClose={hideCookieSettings}
                        width={28}
                        height={28}
                    />
                </div>
            </div>
        </div>
    );
}
